import axios from '../api/axiosInstance';
import { useEffect, useState, useMemo } from "react";
import { Container, Stack, Card, Typography, FormControl, InputLabel, Select, MenuItem,Box} from "@mui/material";

import { Search as SearchIcon } from "@mui/icons-material";
import ResponsiveIconButton from "../components/UI/ResponsiveIconButton";
import SearchMovieModal from "./components/SearchMovieModal";
import MovieCard from "./components/MovieCard";
import MovieScreenings from "./components/MovieScreenings";
import MovieDetails from "./components/MovieDetails";

import {filterAndUniqueMovies, groupScreeningsByMovie} from "../utils";
import Home_page_image from '../assets/Home_page_image.webp';
import { useAuth } from '../context/AuthProvider';
import type { Movie, Cinema, Screening } from '../types';


const Reservation = () => {
   const { currentUser } = useAuth()
   const isAdmin = (currentUser?.role_id ?? 0) >= 2;

   const [movies, setMovies] = useState<Movie[]>([]);
   const [cinemas, setCinemas] = useState<Cinema[]>([]);

   const [selectedCinema, setSelectedCinema] = useState<Cinema | null>(null);
   const [selectedMovieId, setSelectedMovieId] = useState(-1);

   const [nbrOfTickets, setNbrOfTickets] = useState(1);
   const [modalOpen, setModalOpen] = useState(false);

   // Initial fetch
   useEffect(() => {
      const fetchInitialData = async () => {
         try {
            const [moviesRes, cinemaRes] = await Promise.all([
               axios.get(isAdmin ? "/api/v1/movies/upcoming/all" : "/api/v1/movies/upcoming"),
               axios.get("/api/v1/cinemas"),
            ]);

            setMovies(moviesRes.data);
            setCinemas(cinemaRes.data);
         } catch (error) {
         console.error("Error fetching initial data:", error);
         }
      };
      fetchInitialData();
   }, [isAdmin]);

   const moviesToDisplay = useMemo(
      () => filterAndUniqueMovies(movies, { selectedCinema }),
      [movies, selectedCinema]
   );

   const screeningsToDisplay = useMemo(() => groupScreeningsByMovie(movies as unknown as Screening[]), [movies]);


   useEffect(() => {
      if (selectedMovieId === -1 ) return;
      const scrollToBottom = () => {
         window.scrollTo({top: document.body.scrollHeight,behavior: "smooth",});
      };
      const frame = requestAnimationFrame(scrollToBottom);
      return () => cancelAnimationFrame(frame);
   }, [selectedMovieId, screeningsToDisplay]);


   return (
      <Container sx={{ flexGrow: 1, py: 4, display: "flex", flexDirection: "column", gap: 1 }}>
         <Card>
            <Stack direction="row" spacing={2} alignItems="stretch">
               <Box
               component="img"
               src={Home_page_image}
               alt="Home Image"
               width="100%"
               />
            </Stack>
         </Card>
         <Card sx={{ p: 2 }}>
         <Typography variant="h4" gutterBottom>Reservation</Typography>
         <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'stretch' }}>
            {/* Cinema Select and Search Button group */}
            <Stack direction="row" spacing={2} alignItems="stretch" sx={{ flexGrow: { xs: 1, md: 0 } }}>
               <FormControl sx={{ width: { xs: '100%', md: 360 } }}>
                  <InputLabel id="cinema-select-label">Cinema</InputLabel>
                  <Select
                  labelId="cinema-select-label"
                  value={selectedCinema?.cinema_id || ""}
                  label="Cinema"
                  onChange={(e) => {
                     const selected = cinemas.find(c => c.cinema_id === Number(e.target.value));
                     setSelectedCinema(selected || null);
                     setSelectedMovieId(-1)
                  }}
                  >
                  <MenuItem value="">All</MenuItem>
                  {cinemas.map(cinema => (
                     <MenuItem key={cinema.cinema_id} value={cinema.cinema_id}>
                        {cinema.cinema_name}
                     </MenuItem>
                  ))}
                  </Select>
               </FormControl>

               <ResponsiveIconButton
                  size="large"
                  variant="outlined"
                  onClick={() => setModalOpen(true)}
                  icon={<SearchIcon />}
               >
                  Find movie
               </ResponsiveIconButton>

               <SearchMovieModal modalOpen={modalOpen} setModalOpen={setModalOpen} />
            </Stack>

            {/* Tickets - responsive positioning */}
            <FormControl sx={{ width: { xs: '100%', md: 150 } }}>
               <InputLabel id="number-of-tickets-select-label">Tickets</InputLabel>
               <Select
               labelId="number-of-tickets-select-label"
               value={nbrOfTickets}
               label="Tickets"
               onChange={(e) => setNbrOfTickets(Number(e.target.value))}
               >
               {[...Array(10)].map((_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                     {i + 1}
                  </MenuItem>
               ))}
               </Select>
            </FormControl>
         </Stack>
         </Card>

         <Typography variant="h5">Airing now</Typography>

         <Stack gap={2} justifyContent="flex-start" direction="row" flexWrap="wrap">
         {moviesToDisplay.map((movie) => (
            <Box onClick={ () => {setSelectedMovieId(movie.movie_id)} }  key={movie.movie_id}>
               <MovieCard movie={movie}/>
            </Box>
         ))}
         </Stack>

         {selectedMovieId !== -1 && screeningsToDisplay[selectedMovieId] && (
         <>
            <MovieDetails movie={screeningsToDisplay[selectedMovieId][0] as unknown as Movie} loadingMovie={false} />
            <MovieScreenings movieId={selectedMovieId} cinema_id={selectedCinema?.cinema_id || null} nbrOfTickets={nbrOfTickets} />
         </>
         )}
      </Container>
   );
};

export default Reservation;
